import React, { useState, useMemo, useEffect, useCallback, useDeferredValue, useRef } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { WordItem } from '../types';
import { ALL_UNIQUE_WORDS, SORTED_CATEGORIES } from '../data/gameData';
import { playSound } from '../audio';
import { useKabyleTTS, hasRecordedAudio, getRecordedAudiosSet } from '../hooks/useKabyleTTS';
import { getWordIcon } from '../utils/emojis';
import { getFavoriteWords, isFavoriteWord, toggleFavoriteWord } from '../utils/favorites';
import { saveWordOverride, resetWordOverride, subscribeWordOverrides, deleteWordFromDictionary } from '../utils/wordOverridesStorage';
import { BerberRugBorder } from './Sprites';
import { TTSButton } from './TTSButton';
import { AudioRecorderSection } from './AudioRecorderSection';
import { MusicToggleButton } from './MusicToggleButton';
import { PronunciationGuide } from './PronunciationGuide';
import { FavoriteChallengeView } from './FavoriteChallengeView';
import { CustomVocabularyTab } from './CustomVocabularyTab';
import { getCustomWords, subscribeCustomWords, MAX_CUSTOM_WORDS } from '../utils/customVocabularyStorage';
import { Heart, Sparkles, Award, Edit3, BookOpen, Plus, ChevronDown, Layers, Check, X, Search, Filter, Info, Lightbulb, Trash2 } from 'lucide-react';
import { CrosswordGrid } from '../data/crosswordData';
import { generateCrosswordForChallenge, generateCrosswordGridsForChallenge } from '../utils/crosswordGenerator';
import { CrosswordChallengeView } from './CrosswordChallengeView';
import { AddPublicWordModal } from './AddPublicWordModal';
import { AdminWordEditorModal } from './AdminWordEditorModal';
import { UnifiedQuizChallengeModal } from './UnifiedQuizChallengeModal';
import { ConjugationBox } from './ConjugationBox';
import { getChallengeWords } from '../utils/challengeWords';
import { getChallengeByCategory, ChallengeItem } from '../data/challengesData';
import { CATEGORY_GRAMMAR_TIPS } from '../data/grammarTips';

export const CATEGORY_ICONS: Record<string, string> = {
  'Animaux': '🐾',
  'Couleurs': '🎨',
  'Histoires & École': '📚',
  'Jeux & Transports': '🚗',
  'La Famille': '👨‍👩‍👧',
  'La Maison': '🏡',
  'Le Corps & Soins': '🩺',
  'Le Temps': '⏳',
  'Lieux & Espace': '🗺️',
  'Mots de liaison': '🔗',
  'Double Pronominalisation': '🔀',
  'Nature & Météo': '🌿',
  'Nombres & Quantité': '🔢',
  'Nourriture': '🥘',
  'Pensée & Communication': '💭',
  'Phrases & Quotidien': '💬',
  'Pronoms & Personnes': '👥',
  'Questions & Réponses': '❓',
  'Salutations & Politesse': '🤝',
  'Verbes au Futur & Présent': '⏩',
  'Verbes au Passé': '⏪',
  'Verbes': '⚡',
  "Verbes d'Action": '⚡',
  'Émotions & Sensations': '💖',
  'TOUS': '🌐',
  'FAVORIS': '❤️',
  'SINGULIER_PLURIEL': '📖',
};


interface DictionaryViewProps {
  onBack: () => void;
  isAdmin?: boolean;
  score?: number;
  onAddScore?: (xp: number) => void;
  playerId?: string;
  playerName?: string;
}

interface WordCardProps {
  word: WordItem;
  isAdmin?: boolean;
  isFavorite: boolean;
  hasAudio?: boolean;
  onToggleFavorite: (kab: string) => void;
  onPlay: (text: string) => void;
  onEdit?: (word: WordItem) => void;
  onDelete?: (word: WordItem) => void;
}

const cleanBestTranslation = (fr: string): string => {
  if (!fr) return '';
  if (fr.includes('/')) {
    return fr.split('/')[0].trim();
  }
  return fr;
};

const WordCard: React.FC<WordCardProps> = React.memo(({ word, isAdmin, isFavorite, hasAudio: propHasAudio, onToggleFavorite, onPlay, onEdit, onDelete }) => {
  const hasAudio = propHasAudio !== undefined ? propHasAudio : hasRecordedAudio(word.kab);
  const icon = getWordIcon(word);
  const displayFr = cleanBestTranslation(word.fr);

  return (
    <div
      onClick={() => onPlay(word.kab)}
      className={`p-2.5 rounded-xl transition-all flex flex-col justify-between gap-1.5 cursor-pointer group relative ${
        isAdmin
          ? hasAudio
            ? 'bg-emerald-50/90 hover:bg-emerald-100/90 border-2 border-emerald-600 shadow-xs'
            : 'bg-amber-50/90 hover:bg-amber-100/90 border-2 border-amber-600 shadow-xs'
          : isFavorite
            ? 'bg-[#fffafa] hover:bg-white border-2 border-rose-400 shadow-[0_2px_8px_rgba(244,63,94,0.12)]'
            : 'bg-white/95 hover:bg-[#fffdf9] border border-[#e5dcce] hover:border-amber-600 shadow-[0_2px_6px_rgba(180,83,9,0.06)] hover:shadow-[0_4px_12px_rgba(180,83,9,0.1)]'
      }`}
      title={`Cliquer pour écouter « ${word.kab} »`}
    >
      {/* Top line: Word + category tag + actions */}
      <div className="flex items-center justify-between gap-2">
        <span className="font-black text-stone-900 text-sm sm:text-base group-hover:text-amber-800 transition-colors flex items-center gap-1.5 truncate">
          {icon && <span className="text-lg shrink-0">{icon}</span>}
          <span className="tracking-wide truncate">{word.kab}</span>
        </span>

        <div className="flex items-center gap-1.5 shrink-0">
          {/* Favorite Heart Button with enhanced touch target */}
          <button
            type="button"
            aria-label={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris'}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(word.kab);
            }}
            className={`p-2 min-w-[38px] min-h-[38px] flex items-center justify-center rounded-xl transition-all cursor-pointer ${
              isFavorite
                ? 'bg-rose-100 text-rose-600 border border-rose-300 hover:bg-rose-200 shadow-xs scale-105'
                : 'bg-amber-50/80 text-stone-400 hover:text-rose-500 hover:bg-rose-50 border border-amber-200/80 active:scale-95'
            }`}
            title={isFavorite ? 'Retirer des favoris' : 'Ajouter aux favoris ❤️'}
          >
            <Heart className={`w-4 h-4 ${isFavorite ? 'fill-rose-500' : ''}`} />
          </button>

          {isAdmin && (
            <span
              className={`w-2.5 h-2.5 rounded-full ${
                hasAudio ? 'bg-emerald-600 animate-pulse' : 'bg-amber-600'
              }`}
              title={hasAudio ? 'Audio personnalisé actif' : "En attente d'enregistrement"}
            />
          )}

          <TTSButton text={word.kab} size="sm" className="bg-amber-50 hover:bg-amber-100 text-amber-900 border border-amber-300/80" />
          
          {isAdmin && onEdit && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onEdit(word);
              }}
              className="p-1.5 min-w-[36px] min-h-[36px] flex items-center justify-center bg-amber-600 hover:bg-amber-700 rounded-lg text-xs font-bold text-white border border-amber-500 cursor-pointer shadow-xs active:scale-95 transition-all"
              title="Éditer et enregistrer l'audio"
            >
              ✏️
            </button>
          )}

          {isAdmin && onDelete && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onDelete(word);
              }}
              className="p-1.5 min-w-[36px] min-h-[36px] flex items-center justify-center bg-red-100 hover:bg-red-200 text-red-700 hover:text-red-800 rounded-lg text-xs font-bold border border-red-300 cursor-pointer shadow-xs active:scale-95 transition-all"
              title="Supprimer ce mot du dictionnaire"
            >
              🗑️
            </button>
          )}
        </div>
      </div>

      {/* Middle line: French translation + grammatical gender badge */}
      <div className="flex items-center justify-between text-xs sm:text-sm pt-1.5 border-t border-amber-800/10 gap-2">
        <span className="italic text-stone-700 font-semibold truncate">{displayFr}</span>
        {word.gender && (word.gender === 'm' || word.gender === 'f') && (
          <span
            className={`text-[10px] font-black px-2 py-0.5 rounded-md uppercase shrink-0 ${
              word.gender === 'f'
                ? 'bg-rose-100 text-rose-900 border border-rose-300'
                : 'bg-sky-100 text-sky-900 border border-sky-300'
            }`}
            title={word.gender === 'f' ? 'Nom Féminin' : 'Nom Masculin'}
          >
            {word.gender === 'f' ? 'fém.' : 'masc.'}
          </span>
        )}
      </div>

      {/* Declensions: Singular / Plural Form */}
      {(() => {
        const showPlural =
          Boolean(word.pluralKab) &&
          word.pluralKab!.toLowerCase().trim() !== word.kab.toLowerCase().trim() &&
          word.grammaticalNumber !== 'pl';

        const showSingular =
          Boolean(word.singularKab) &&
          word.singularKab!.toLowerCase().trim() !== word.kab.toLowerCase().trim() &&
          word.grammaticalNumber !== 'sg';

        if (!showPlural && !showSingular) return null;

        return (
          <div className="flex flex-wrap items-center gap-1.5 pt-1.5 text-xs border-t border-amber-800/10 mt-0.5">
            {showPlural && (
              <div className="inline-flex items-center gap-1 bg-amber-50 border border-amber-300/80 px-2 py-0.5 rounded-md text-amber-950 shadow-2xs">
                <span className="text-[10px] font-black text-amber-800 uppercase tracking-wider">Pluriel :</span>
                <strong className="text-stone-900 font-bold">{word.pluralKab}</strong>
              </div>
            )}

            {showSingular && (
              <div className="inline-flex items-center gap-1 bg-emerald-50 border border-emerald-300/80 px-2 py-0.5 rounded-md text-emerald-950 shadow-2xs">
                <span className="text-[10px] font-black text-emerald-800 uppercase tracking-wider">Singulier :</span>
                <strong className="text-stone-900 font-bold">{word.singularKab}</strong>
              </div>
            )}
          </div>
        );
      })()}

      {/* Word Variants (Tamazight / Dialects / Synonyms) */}
      {Boolean(word.variants && word.variants.trim()) && (
        <div className="pt-1.5 text-xs border-t border-amber-800/10 mt-0.5 flex items-baseline gap-1.5 flex-wrap">
          <span className="text-[10px] font-bold text-amber-800 uppercase tracking-wider bg-amber-100/70 border border-amber-300/60 px-1.5 py-0.5 rounded">
            Variante{word.variants!.includes(',') || word.variants!.includes('/') ? 's' : ''} :
          </span>
          <span className="text-stone-800 font-semibold italic text-xs">
            {word.variants}
          </span>
        </div>
      )}

      {/* Conjugation (Inaccompli & Accompli) - Visible uniquement pour la catégorie Verbes */}
      {((word.type || '').trim() === 'Verbes' || (word.type || '').trim() === "Verbes d'Action" || (word.type || '').toLowerCase().includes('verbe')) &&
        (Boolean(word.conjugationAccompli && word.conjugationAccompli.trim()) || Boolean(word.conjugationInaccompli && word.conjugationInaccompli.trim())) && (
        <ConjugationBox
          inaccompli={word.conjugationInaccompli}
          accompli={word.conjugationAccompli}
        />
      )}
    </div>
  );
});

export const DictionaryView: React.FC<DictionaryViewProps> = React.memo(({
  onBack,
  isAdmin,
  score = 0,
  onAddScore,
  playerId,
  playerName,
}) => {
  const [activeDictionaryTab, setActiveDictionaryTab] = useState<'VOCABULARY' | 'CUSTOM_VOCABULARY' | 'PRONUNCIATION'>('VOCABULARY');
  const [customWordsCount, setCustomWordsCount] = useState<number>(() => getCustomWords(playerId).length);
  const [editingWord, setEditingWord] = useState<WordItem | null>(null);
  const { playTTS } = useKabyleTTS();
  const [updateCounter, setUpdateCounter] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('TOUS');
  const [isCategoryDrawerOpen, setIsCategoryDrawerOpen] = useState(false);
  const [categoryDrawerSearch, setCategoryDrawerSearch] = useState('');
  const [audioFilter, setAudioFilter] = useState<'ALL' | 'WITH_AUDIO' | 'WITHOUT_AUDIO'>('ALL');
  const [favoriteWordsSet, setFavoriteWordsSet] = useState<Set<string>>(() => new Set(getFavoriteWords()));
  const [showFavoriteChallenge, setShowFavoriteChallenge] = useState(false);
  const [activeThematicChallenge, setActiveThematicChallenge] = useState<ChallengeItem | null>(null);
  const [activeCrosswordGrid, setActiveCrosswordGrid] = useState<CrosswordGrid | null>(null);
  const [activeCrosswordGrids, setActiveCrosswordGrids] = useState<CrosswordGrid[]>([]);
  const [isAddWordModalOpen, setIsAddWordModalOpen] = useState(false);
  const [showFavoritesTooltip, setShowFavoritesTooltip] = useState(false);
  const [wordToDelete, setWordToDelete] = useState<WordItem | null>(null);
  const [isDeletingWord, setIsDeletingWord] = useState(false);
  const [expandedTips, setExpandedTips] = useState<Record<string, boolean>>({});

  const toggleTip = useCallback((cat: string) => {
    playSound('click');
    setExpandedTips((prev) => ({
      ...prev,
      [cat]: !prev[cat],
    }));
  }, []);

  const currentThematicChallenge = useMemo(() => {
    if (!selectedCategory || selectedCategory === 'TOUS' || selectedCategory === 'FAVORIS' || selectedCategory === 'SINGULIER_PLURIEL') {
      return undefined;
    }
    return getChallengeByCategory(selectedCategory);
  }, [selectedCategory]);

  // Close category drawer on Escape key
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isCategoryDrawerOpen) {
        setIsCategoryDrawerOpen(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isCategoryDrawerOpen]);

  // First-time coaching overlay for favorites (heart)
  const [showHeartCoachingOverlay, setShowHeartCoachingOverlay] = useState<boolean>(() => {
    try {
      return !localStorage.getItem('tamazight_dict_heart_hint_seen');
    } catch {
      return false;
    }
  });

  const dismissHeartOverlay = useCallback(() => {
    setShowHeartCoachingOverlay(false);
    try {
      localStorage.setItem('tamazight_dict_heart_hint_seen', 'true');
    } catch {}
  }, []);

  // Use a ref to store the tooltip timeout to allow clearing it
  const tooltipTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Sync custom words count for active player
  useEffect(() => {
    // Cleanup timeout on unmount
    return () => {
      if (tooltipTimeoutRef.current) {
        clearTimeout(tooltipTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    setCustomWordsCount(getCustomWords(playerId).length);
    const handleCustomWordsUpdate = (detail?: any) => {
      if (detail && detail.playerId && playerId && detail.playerId !== playerId) {
        return;
      }
      setCustomWordsCount(getCustomWords(playerId).length);
    };
    const unsubscribe = subscribeCustomWords(handleCustomWordsUpdate);
    return () => unsubscribe();
  }, [playerId]);

  // Sync favorites across changes
  useEffect(() => {
    const handleFavoritesUpdate = () => {
      setFavoriteWordsSet(new Set(getFavoriteWords()));
    };
    window.addEventListener('kabyle_favorites_updated', handleFavoritesUpdate);
    return () => window.removeEventListener('kabyle_favorites_updated', handleFavoritesUpdate);
  }, []);

  // Listen to live word database updates from server
  useEffect(() => {
    const unsubscribe = subscribeWordOverrides(() => {
      setUpdateCounter((c) => c + 1);
    });
    return () => unsubscribe();
  }, []);

  // Listen to audio changes from storage
  useEffect(() => {
    const handleAudioUpdate = () => {
      setUpdateCounter((c) => c + 1);
    };
    window.addEventListener('kabyle_audio_updated', handleAudioUpdate);
    return () => window.removeEventListener('kabyle_audio_updated', handleAudioUpdate);
  }, []);

  const listenedWordsRef = useRef<Set<string>>(new Set());

  const handlePlayWord = useCallback((text: string) => {
    playTTS(text);
    if (onAddScore && text && !listenedWordsRef.current.has(text)) {
      listenedWordsRef.current.add(text);
      onAddScore(25);
    }
  }, [playTTS, onAddScore]);

  const handleToggleFavorite = useCallback((kab: string) => {
    const isNowFav = toggleFavoriteWord(kab);
    playSound('click');

    if (isNowFav) {
      if (!tooltipTimeoutRef.current) {
        setShowFavoritesTooltip(true);
        tooltipTimeoutRef.current = setTimeout(() => {
          setShowFavoritesTooltip(false);
          tooltipTimeoutRef.current = null;
        }, 5000); // 5 seconds
      }
    }

    setFavoriteWordsSet((prev) => {
      const next = new Set(prev);
      if (isNowFav) {
        next.add(kab);
      } else {
        next.delete(kab);
      }
      return next;
    });
  }, []);

  const handleEditWord = useCallback((word: WordItem) => {
    setEditingWord(word);
  }, []);

  const handleDeleteWordDirect = useCallback((word: WordItem) => {
    playSound('click');
    setWordToDelete(word);
  }, []);

  const handleConfirmDeleteWord = useCallback(async () => {
    if (!wordToDelete) return;
    playSound('click');
    setIsDeletingWord(true);
    try {
      await deleteWordFromDictionary(wordToDelete);
      setUpdateCounter((c) => c + 1);
      setWordToDelete(null);
    } catch (err) {
      console.error('Failed to delete word:', err);
    } finally {
      setIsDeletingWord(false);
    }
  }, [wordToDelete]);

  // Pre-index recorded audios into a Set for instantaneous O(1) checks
  const recordedAudiosSet = useMemo(() => {
    return getRecordedAudiosSet();
  }, [updateCounter]);

  // Compute total audio stats
  const totalRecordedCount = useMemo(() => {
    return recordedAudiosSet.size;
  }, [recordedAudiosSet]);

  // Compute list of full WordItem objects for all favorites
  const favoriteWordItems = useMemo(() => {
    return ALL_UNIQUE_WORDS.filter((w) => favoriteWordsSet.has(w.kab));
  }, [favoriteWordsSet, updateCounter]);

  const handleStartCrosswordFromFavorites = useCallback(() => {
    if (favoriteWordItems.length === 0) return;
    playSound('stele');
    const grids = generateCrosswordGridsForChallenge(
      'fav_crossword_user_favorites',
      'Mots-Croisés • Mes Favoris',
      'Favoris',
      favoriteWordItems,
      {
        icon: '❤️',
        region: 'Amawal Favoris',
        requiredLevel: 0,
      }
    );
    setActiveCrosswordGrids(grids);
    setActiveCrosswordGrid(grids[0] || null);
  }, [favoriteWordItems]);

  // Deferred search term so typing stays 120fps smooth
  const deferredSearchTerm = useDeferredValue(searchTerm);

  const singularPluralCount = useMemo(() => {
    return ALL_UNIQUE_WORDS.filter((w) => {
      const hasPlural =
        Boolean(w.pluralKab) &&
        w.pluralKab!.toLowerCase().trim() !== w.kab.toLowerCase().trim() &&
        w.grammaticalNumber !== 'pl';
      const hasSingular =
        Boolean(w.singularKab) &&
        w.singularKab!.toLowerCase().trim() !== w.kab.toLowerCase().trim() &&
        w.grammaticalNumber !== 'sg';
      return hasPlural || hasSingular;
    }).length;
  }, [updateCounter]);

  const filteredWords = useMemo(() => {
    const term = deferredSearchTerm.trim().toLowerCase();
    return ALL_UNIQUE_WORDS.filter((word) => {
      // Favorites filter mode
      if (selectedCategory === 'FAVORIS') {
        if (!favoriteWordsSet.has(word.kab)) return false;
      } else if (selectedCategory === 'SINGULIER_PLURIEL') {
        const hasPlural =
          Boolean(word.pluralKab) &&
          word.pluralKab!.toLowerCase().trim() !== word.kab.toLowerCase().trim() &&
          word.grammaticalNumber !== 'pl';
        const hasSingular =
          Boolean(word.singularKab) &&
          word.singularKab!.toLowerCase().trim() !== word.kab.toLowerCase().trim() &&
          word.grammaticalNumber !== 'sg';
        if (!hasPlural && !hasSingular) return false;
      } else {
        const matchesCat = selectedCategory === 'TOUS' || word.type === selectedCategory;
        if (!matchesCat) return false;
      }

      const hasAudio = recordedAudiosSet.has(word.kab.toLowerCase().trim());
      if (audioFilter === 'WITH_AUDIO' && !hasAudio) return false;
      if (audioFilter === 'WITHOUT_AUDIO' && hasAudio) return false;

      if (!term) return true;
      return (
        word.kab.toLowerCase().includes(term) ||
        word.fr.toLowerCase().includes(term) ||
        word.type.toLowerCase().includes(term) ||
        (word.pluralKab && word.pluralKab.toLowerCase().includes(term)) ||
        (word.pluralFr && word.pluralFr.toLowerCase().includes(term)) ||
        (word.singularKab && word.singularKab.toLowerCase().includes(term)) ||
        (word.singularFr && word.singularFr.toLowerCase().includes(term)) ||
        (word.feminineKab && word.feminineKab.toLowerCase().includes(term)) ||
        (word.masculineKab && word.masculineKab.toLowerCase().includes(term))
      );
    });
  }, [deferredSearchTerm, selectedCategory, audioFilter, recordedAudiosSet, favoriteWordsSet, updateCounter]);

  // Progressive loading: initial 60 words for snappy rendering, with a "Tout afficher" button
  const [visibleLimit, setVisibleLimit] = useState<number>(60);

  useEffect(() => {
    setVisibleLimit(60);
  }, [deferredSearchTerm, selectedCategory, audioFilter]);

  const { displayedGroupedWords, displayedTotalCount, totalMatchingCount } = useMemo(() => {
    let count = 0;
    const result: Record<string, WordItem[]> = {};
    const totalMatching = filteredWords.length;

    for (const word of filteredWords) {
      if (count < visibleLimit) {
        if (!result[word.type]) result[word.type] = [];
        result[word.type].push(word);
        count++;
      }
    }
    return {
      displayedGroupedWords: result,
      displayedTotalCount: count,
      totalMatchingCount: totalMatching,
    };
  }, [filteredWords, visibleLimit]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    ALL_UNIQUE_WORDS.forEach((w) => {
      counts[w.type] = (counts[w.type] || 0) + 1;
    });
    return counts;
  }, [updateCounter]);

  const displayedCategories = useMemo(
    () => Object.keys(displayedGroupedWords).sort(),
    [displayedGroupedWords]
  );

  const filteredDrawerCategories = useMemo(() => {
    const q = categoryDrawerSearch.trim().toLowerCase();
    if (!q) return SORTED_CATEGORIES;
    return SORTED_CATEGORIES.filter((cat) => cat.toLowerCase().includes(q));
  }, [categoryDrawerSearch]);

  if (activeCrosswordGrid) {
    return (
      <div className="fixed inset-0 w-full h-[100dvh] bg-[#0b1329] text-white flex flex-col z-50 overflow-hidden select-none font-mono">
        <CrosswordChallengeView
          score={score}
          challengeGrid={activeCrosswordGrid}
          challengeGrids={activeCrosswordGrids}
          challengeTitle="Mots-Croisés • Mes Favoris"
          onBack={() => setActiveCrosswordGrid(null)}
          onAddScore={onAddScore}
        />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 w-full h-[100dvh] bg-parchment text-stone-900 flex flex-col z-50 overflow-hidden select-none font-mono">
      {/* Top Authentic Berber Woven Rug Border */}
      <BerberRugBorder />

      {/* Header with Warm Parchment Aesthetic */}
      <header className="py-2 px-3 sm:px-4 bg-[#fcf8f0] border-b border-[#dfd2be] shadow-xs flex flex-col gap-2.5 flex-shrink-0 z-30">
        {/* Row 1: Back Button & Actions (Music / Favorites Challenge / Admin) */}
        <div className="flex items-center justify-between gap-2 w-full">
          <button
            onClick={() => {
              playSound('click');
              onBack();
            }}
            className="px-3.5 py-1.5 bg-amber-900 hover:bg-amber-800 text-amber-50 font-black text-xs uppercase tracking-wider border border-amber-700 rounded-lg shadow-xs active:translate-y-0.5 transition-all flex items-center gap-1.5 shrink-0 cursor-pointer"
          >
            <svg className="w-3 h-3 fill-amber-50 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
              <polygon points="17,4 7,12 17,20" />
            </svg>
            <span>Retour</span>
          </button>

          <div className="flex items-center gap-1.5 shrink-0 flex-wrap justify-end">
            {/* Add Public Word Button (Admin) */}
            {isAdmin && (
              <button
                onClick={() => {
                  playSound('click');
                  setIsAddWordModalOpen(true);
                }}
                className="px-2.5 py-1.5 bg-gradient-to-r from-emerald-700 to-teal-700 hover:from-emerald-600 hover:to-teal-600 text-white border border-emerald-500 rounded-lg text-xs font-black uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-all shadow-xs active:translate-y-0.5"
                title="Ajouter un mot au dictionnaire public"
              >
                <span>+ Mot Public</span>
              </button>
            )}

            {/* Music Toggle */}
            <MusicToggleButton variant="compact" />

          </div>
        </div>

        {/* Row 2: Full-Width 3-Segment Tab Switcher (Dico | Mes Mots | Lire) - Matching Inventory tabs */}
        <div className="w-full flex p-1 gap-1 sm:gap-1.5 bg-[#ede4d4] rounded-xl border border-[#d6c7b0] shadow-inner shrink-0">
          <button
            onClick={() => {
              playSound('click');
              setActiveDictionaryTab('VOCABULARY');
            }}
            className={"flex-1 py-1.5 sm:py-2 px-2 text-xs sm:text-sm font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 " + (activeDictionaryTab === 'VOCABULARY' ? 'bg-amber-700 text-white font-black shadow-xs' : 'text-stone-700 hover:text-stone-950 hover:bg-amber-100/70')}
          >
            <span>📚</span>
            <span className="truncate">Dico</span>
          </button>

          <button
            onClick={() => {
              playSound('click');
              setActiveDictionaryTab('CUSTOM_VOCABULARY');
            }}
            className={"flex-1 py-1.5 sm:py-2 px-2 text-xs sm:text-sm font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 " + (activeDictionaryTab === 'CUSTOM_VOCABULARY' ? 'bg-amber-800 text-white font-black shadow-xs' : 'text-stone-700 hover:text-stone-950 hover:bg-amber-100/70')}
          >
            <span>✍️</span>
            <span className="truncate">Mes Mots</span>
          </button>

          <button
            onClick={() => {
              playSound('click');
              setActiveDictionaryTab('PRONUNCIATION');
            }}
            className={"flex-1 py-1.5 sm:py-2 px-2 text-xs sm:text-sm font-bold uppercase tracking-wider rounded-lg transition-all cursor-pointer flex items-center justify-center gap-1.5 " + (activeDictionaryTab === 'PRONUNCIATION' ? 'bg-amber-700 text-white font-black shadow-xs' : 'text-stone-700 hover:text-stone-950 hover:bg-amber-100/70')}
          >
            <span>🗣️</span>
            <span className="truncate">Prononcer</span>
          </button>
        </div>

        {/* Row 3: Search & Filter controls (Only for VOCABULARY tab) */}
        {activeDictionaryTab === 'VOCABULARY' && (
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 w-full">
            {/* Search Input */}
            <div className="relative flex-1">
              <input
                type="text"
                placeholder="Rechercher un mot en Kabyle, Français ou Catégorie..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-white border border-[#d6c7b2] rounded-xl px-3.5 py-2.5 pl-9 text-base text-stone-900 focus:outline-none focus:border-amber-700 font-mono placeholder-stone-400 shadow-2xs min-h-[44px]"
              />
              <span className="absolute left-3 top-3 text-stone-400 text-sm pointer-events-none">🔍</span>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-2.5 text-stone-500 hover:text-stone-800 text-xs bg-stone-100 px-2 py-1 rounded-md border border-stone-300 cursor-pointer min-h-[28px]"
                >
                  ✕
                </button>
              )}
            </div>

            {/* Single Category Volet Trigger Button */}
            <button
              type="button"
              onClick={() => {
                playSound('click');
                setIsCategoryDrawerOpen(true);
              }}
              className="px-3.5 py-2 bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-700 hover:to-amber-800 text-white font-bold text-xs sm:text-sm rounded-xl border border-amber-950 shadow-xs flex items-center justify-between sm:justify-center gap-2 cursor-pointer active:scale-95 transition-all shrink-0 ring-1 ring-amber-600/40 min-h-[44px]"
              title="Ouvrir le volet des catégories"
            >
              <div className="flex items-center gap-2 truncate">
                <span className="text-base shrink-0">
                  {selectedCategory === 'TOUS'
                    ? '🌐'
                    : selectedCategory === 'FAVORIS'
                    ? '❤️'
                    : selectedCategory === 'SINGULIER_PLURIEL'
                    ? '📖'
                    : CATEGORY_ICONS[selectedCategory] || '📁'}
                </span>
                <span className="truncate max-w-[140px] sm:max-w-[180px] font-bold">
                  {selectedCategory === 'TOUS'
                    ? 'Toutes Catégories'
                    : selectedCategory === 'FAVORIS'
                    ? 'Mes Favoris'
                    : selectedCategory === 'SINGULIER_PLURIEL'
                    ? 'Singulier / Pluriel'
                    : selectedCategory}
                </span>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <span className="text-xs px-2 py-0.5 rounded-full bg-black/25 text-amber-200 font-black">
                  {selectedCategory === 'TOUS'
                    ? ALL_UNIQUE_WORDS.length
                    : selectedCategory === 'FAVORIS'
                    ? favoriteWordItems.length
                    : selectedCategory === 'SINGULIER_PLURIEL'
                    ? singularPluralCount
                    : categoryCounts[selectedCategory] || 0}
                </span>
                <ChevronDown className="w-4 h-4 text-amber-300" />
              </div>
            </button>

            {/* Quick Filter Pills (Audio / Admin) */}
            {isAdmin && (
              <div className="flex items-center gap-1 shrink-0 bg-[#ede4d4] p-0.5 rounded-lg border border-[#d6c7b0] text-[10px]">
                <button
                  onClick={() => setAudioFilter('ALL')}
                  className={`px-2 py-1 rounded font-bold cursor-pointer transition-all ${
                    audioFilter === 'ALL' ? 'bg-amber-700 text-white' : 'text-stone-700 hover:text-stone-950'
                  }`}
                >
                  Tous ({ALL_UNIQUE_WORDS.length})
                </button>
                <button
                  onClick={() => setAudioFilter('WITH_AUDIO')}
                  className={`px-2 py-1 rounded font-bold cursor-pointer transition-all flex items-center gap-1 ${
                    audioFilter === 'WITH_AUDIO' ? 'bg-emerald-700 text-white' : 'text-emerald-800 hover:text-emerald-950'
                  }`}
                >
                  <span>🎤 Avec audio</span>
                  <span className="text-[9px]">({totalRecordedCount})</span>
                </button>
                <button
                  onClick={() => setAudioFilter('WITHOUT_AUDIO')}
                  className={`px-2 py-1 rounded font-bold cursor-pointer transition-all flex items-center gap-1 ${
                    audioFilter === 'WITHOUT_AUDIO' ? 'bg-amber-600 text-white' : 'text-amber-800 hover:text-amber-950'
                  }`}
                >
                  <span>⏳ Manquants</span>
                  <span className="text-[9px]">({ALL_UNIQUE_WORDS.length - totalRecordedCount})</span>
                </button>
              </div>
            )}
          </div>
        )}
      </header>

      {/* View Switcher: Pronunciation Guide VS Custom Vocabulary */}
      {activeDictionaryTab === 'PRONUNCIATION' && (
        <PronunciationGuide isAdmin={isAdmin} />
      )}
      {activeDictionaryTab === 'CUSTOM_VOCABULARY' && (
        <CustomVocabularyTab
          allWords={ALL_UNIQUE_WORDS}
          onAddScore={onAddScore}
          playerId={playerId}
          playerName={playerName}
        />
      )}

      {/* Main Content Area - Vocabulary List */}
      {activeDictionaryTab === 'VOCABULARY' && (
        <main id="dictionary-main-scroll" className="flex-1 overflow-y-auto px-2.5 sm:px-5 py-3 space-y-4">
          {/* FAVORITES BANNER / CHALLENGE CARD when in Favoris mode or when favorites exist */}
          {selectedCategory === 'FAVORIS' && favoriteWordItems.length > 0 && (
            <div className="bg-[#fffdf9] border-2 border-rose-400 rounded-2xl p-3.5 sm:p-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-md">
              <div className="flex items-center gap-3 text-center sm:text-left">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-rose-500 to-amber-500 flex items-center justify-center shadow-xs shrink-0 border border-amber-200">
                  <Sparkles className="w-6 h-6 text-white animate-pulse" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-black text-amber-900 uppercase tracking-wide">
                    Prêt pour le Défi Favoris ?
                  </h3>
                  <p className="text-xs text-stone-600 mt-0.5">
                    Tu as sélectionné <span className="text-amber-900 font-bold">{favoriteWordItems.length} mot{favoriteWordItems.length > 1 ? 's' : ''}</span>. Lance une session de quiz interactif pour les mémoriser et gagner de l'XP !
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto">
                <button
                  onClick={handleStartCrosswordFromFavorites}
                  className="flex-1 sm:flex-initial px-4 py-2.5 bg-amber-600 hover:bg-amber-700 text-white font-black text-base uppercase tracking-wider rounded-xl shadow-xs border border-amber-500 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 shrink-0"
                >
                  <span>🧩</span>
                  <span>Mots-Croisés</span>
                </button>

                <button
                  onClick={() => {
                    playSound('click');
                    setShowFavoriteChallenge(true);
                  }}
                  className="flex-1 sm:flex-initial px-4 py-2.5 bg-amber-800 hover:bg-amber-900 text-white font-black text-base uppercase tracking-wider rounded-xl shadow-xs border border-amber-700 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2 shrink-0"
                >
                  <span>⚡</span>
                  <span>Quizz</span>
                </button>
              </div>
            </div>
          )}

          {/* THEMATIC CHALLENGE QUIZ CTA BANNER (Exclusivement pour les catégories thématiques, masqué si recherche textuelle active) */}
          {currentThematicChallenge && !searchTerm.trim() && (
            <div className="bg-[#fffdf9] border-2 border-amber-400/90 rounded-2xl p-3 sm:p-4 flex flex-col sm:flex-row items-center justify-between gap-3 shadow-xs hover:border-amber-500 transition-all">
              <div className="flex items-center gap-3 text-center sm:text-left w-full sm:w-auto">
                <div className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl bg-gradient-to-tr from-amber-500 to-yellow-400 flex items-center justify-center shadow-xs shrink-0 border border-amber-300 text-2xl">
                  {currentThematicChallenge.icon || CATEGORY_ICONS[selectedCategory] || '🎯'}
                </div>
                <div className="min-w-0 flex-1">
                  <h3 className="text-sm sm:text-base font-black text-amber-950 uppercase tracking-wide">
                    Défi Quizz : {currentThematicChallenge.title}
                  </h3>
                  <p className="text-xs text-stone-600 mt-0.5 leading-snug">
                    {currentThematicChallenge.npcName} • {currentThematicChallenge.region} — Entraînez-vous sur les {categoryCounts[selectedCategory] || 0} mots de cette thématique pour valider le défi et gagner de l'XP !
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto justify-end shrink-0">
                <button
                  type="button"
                  onClick={() => {
                    playSound('click');
                    setActiveThematicChallenge(currentThematicChallenge);
                  }}
                  className="w-full sm:w-auto px-4 py-2.5 bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-700 hover:to-amber-800 text-white font-black text-xs sm:text-sm uppercase tracking-wider rounded-xl shadow-xs border border-amber-950 active:scale-95 transition-all cursor-pointer flex items-center justify-center gap-2"
                >
                  <span>⚡</span>
                  <span>Faire le Quizz ({categoryCounts[selectedCategory] || 0} mots)</span>
                </button>
              </div>
            </div>
          )}

          {/* Empty state specifically for Favorites tab */}
          {selectedCategory === 'FAVORIS' && favoriteWordItems.length === 0 ? (
            <div className="bg-[#fffdf9] border-2 border-dashed border-rose-300 rounded-2xl p-6 sm:p-8 text-center max-w-md mx-auto my-8 space-y-4 shadow-sm">
              <div className="w-16 h-16 mx-auto bg-rose-50 rounded-full flex items-center justify-center border-2 border-rose-200 shadow-inner">
                <Heart className="w-8 h-8 text-rose-500 animate-pulse" />
              </div>
              <div className="space-y-1">
                <h3 className="text-base sm:text-lg font-black text-stone-900">Aucun mot favori pour l'instant</h3>
                <p className="text-sm text-stone-600 leading-relaxed">
                  Ajoute des mots en favoris en cliquant sur le cœur <Heart className="inline w-3.5 h-3.5 fill-rose-500 text-rose-500 mx-0.5" /> à côté d'un mot dans le dictionnaire pour composer ton entraînement personnalisé !
                </p>
              </div>
              <button
                onClick={() => {
                  playSound('click');
                  setSelectedCategory('TOUS');
                }}
                className="px-4 py-2 bg-amber-800 hover:bg-amber-900 text-amber-50 rounded-xl text-xs font-bold border border-amber-700 cursor-pointer transition-all"
              >
                Explorer tout le vocabulaire
              </button>
            </div>
          ) : displayedCategories.length === 0 ? (
            <div className="text-center py-12 text-stone-500 space-y-2">
              <p className="text-2xl">🔍</p>
              <p className="text-sm font-bold text-stone-700">Aucun mot trouvé pour votre recherche.</p>
              <button
                onClick={() => {
                  setSearchTerm('');
                  setSelectedCategory('TOUS');
                  setAudioFilter('ALL');
                }}
                className="px-3 py-1 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded text-xs border border-amber-300 cursor-pointer font-bold"
              >
                Réinitialiser la recherche
              </button>
            </div>
          ) : (
            <>
              {displayedCategories.map((category) => {
                const grammarTip = CATEGORY_GRAMMAR_TIPS[category];
                return (
                  <section key={category} className="space-y-2.5">
                    <div className="flex items-center justify-between border-b border-[#dfd2be] pb-1">
                      <h2 className="text-xs font-black uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                        <span className="text-terracotta-600 text-[10px]">◆</span>
                        <span>{category}</span>
                      </h2>
                      <span className="text-[10px] text-stone-500 font-mono">
                        {displayedGroupedWords[category].length} terme{displayedGroupedWords[category].length > 1 ? 's' : ''}
                      </span>
                    </div>

                    {/* Point Info Grammaire & Règles de Temps (dans un accordéon replié de base) */}
                    {grammarTip && !deferredSearchTerm.trim() && (
                      <div className={`rounded-xl border ${grammarTip.borderColor} shadow-2xs overflow-hidden transition-all bg-white/70`}>
                        <button
                          type="button"
                          onClick={() => toggleTip(category)}
                          className="w-full p-2.5 sm:p-3 flex items-center justify-between gap-2.5 text-left hover:bg-amber-100/40 transition-colors cursor-pointer group select-none"
                          aria-expanded={Boolean(expandedTips[category])}
                        >
                          <div className="flex items-center gap-2 sm:gap-2.5 min-w-0">
                            <span className="p-1 rounded-md bg-amber-900/10 text-amber-900 shrink-0 group-hover:bg-amber-900/20 transition-colors">
                              <Info className="w-4 h-4 text-amber-900" />
                            </span>
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h3 className="text-xs sm:text-sm font-black text-stone-900">
                                  {grammarTip.title}
                                </h3>
                                <span className={`text-[9px] sm:text-[10px] font-black px-2 py-0.5 rounded-full border shadow-2xs shrink-0 ${grammarTip.badgeColor}`}>
                                  {grammarTip.badge}
                                </span>
                              </div>
                              <span className="text-[10px] sm:text-[11px] text-amber-900/80 font-medium block">
                                {expandedTips[category]
                                  ? 'Cliquer pour replier les explications'
                                  : 'Point Info & Règles • Cliquer pour déplier les explications'}
                              </span>
                            </div>
                          </div>

                          <div className="flex items-center gap-1.5 shrink-0 pl-2">
                            <span className="text-[11px] font-bold text-amber-900/90 hidden sm:inline">
                              {expandedTips[category] ? 'Masquer' : 'Afficher'}
                            </span>
                            <motion.div
                              animate={{ rotate: expandedTips[category] ? 180 : 0 }}
                              transition={{ duration: 0.2 }}
                              className="p-1 rounded-md bg-amber-100/80 text-amber-900 group-hover:bg-amber-200 transition-colors"
                            >
                              <ChevronDown className="w-4 h-4" />
                            </motion.div>
                          </div>
                        </button>

                        <AnimatePresence initial={false}>
                          {expandedTips[category] && (
                            <motion.div
                              initial={{ height: 0, opacity: 0 }}
                              animate={{ height: 'auto', opacity: 1 }}
                              exit={{ height: 0, opacity: 0 }}
                              transition={{ duration: 0.25, ease: 'easeInOut' }}
                              className="overflow-hidden"
                            >
                              <div className="p-3 sm:p-4 pt-1 sm:pt-1 border-t border-amber-200/60 space-y-2.5">
                                <p className="text-xs text-stone-700 leading-relaxed pt-1">
                                  {grammarTip.summary}
                                </p>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-1">
                                  {grammarTip.rules.map((rule, rIdx) => (
                                    <div key={rIdx} className="bg-white/90 p-2 sm:p-2.5 rounded-lg border border-amber-200/80 text-[11px] space-y-1 shadow-2xs">
                                      <span className="font-black text-amber-950 block">{rule.label}</span>
                                      <p className="text-stone-600 leading-snug">{rule.explanation}</p>
                                      <div className="text-[10px] font-mono text-amber-900 bg-amber-100/60 px-1.5 py-0.5 rounded border border-amber-200/60 mt-1">
                                        {rule.example}
                                      </div>
                                    </div>
                                  ))}
                                </div>

                                {grammarTip.didYouKnow && (
                                  <div className="flex items-center gap-1.5 text-[11px] text-amber-900 font-medium bg-amber-100/70 px-2.5 py-1.5 rounded-md border border-amber-200/60">
                                    <Lightbulb className="w-3.5 h-3.5 text-amber-700 shrink-0" />
                                    <span><strong className="font-bold">Bon à savoir :</strong> {grammarTip.didYouKnow}</span>
                                  </div>
                                )}
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </div>
                    )}

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
                      {displayedGroupedWords[category].map((w, idx) => (
                        <WordCard
                          key={`${w.kab}-${idx}`}
                          word={w}
                          isAdmin={isAdmin}
                          isFavorite={favoriteWordsSet.has(w.kab)}
                          hasAudio={recordedAudiosSet.has(w.kab.toLowerCase().trim())}
                          onToggleFavorite={handleToggleFavorite}
                          onPlay={handlePlayWord}
                          onEdit={handleEditWord}
                          onDelete={handleDeleteWordDirect}
                        />
                      ))}
                    </div>
                  </section>
                );
              })}

              {/* Single Tout afficher button when more words are available */}
              {displayedTotalCount < totalMatchingCount && (
                <div className="pt-4 pb-8 flex flex-col items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => {
                      playSound('click');
                      setVisibleLimit(totalMatchingCount);
                    }}
                    className="px-6 py-2.5 bg-gradient-to-r from-amber-800 to-amber-900 hover:from-amber-700 hover:to-amber-800 text-white font-black text-xs uppercase tracking-wider rounded-xl shadow-md border border-amber-950 transition-all cursor-pointer flex items-center gap-2 active:scale-95 ring-1 ring-amber-600/40"
                  >
                    <span>📜 Tout afficher</span>
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-black/25 text-amber-200 font-mono">
                      {totalMatchingCount} mots
                    </span>
                  </button>
                  <span className="text-[11px] text-stone-500 font-sans">
                    {displayedTotalCount} mots affichés sur {totalMatchingCount}
                  </span>
                </div>
              )}
            </>
          )}
        </main>
      )}

      {/* Favorite Challenge Interactive View */}
      {showFavoriteChallenge && (
        <FavoriteChallengeView
          favoriteWords={favoriteWordItems}
          allWords={ALL_UNIQUE_WORDS}
          onClose={() => setShowFavoriteChallenge(false)}
          onAddScore={onAddScore}
        />
      )}

      {/* Admin Edit Modal */}
      <AdminWordEditorModal
        isOpen={!!editingWord}
        word={editingWord}
        onClose={() => setEditingWord(null)}
        onSuccess={() => setUpdateCounter(c => c + 1)}
      />

      {/* Admin Quick Delete Word Confirmation Dialog */}
      {isAdmin && wordToDelete && (
        <div className="fixed inset-0 z-[130] bg-black/60 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-5 sm:p-6 max-w-sm w-full shadow-2xl border-2 border-red-300 space-y-4 text-center animate-in fade-in zoom-in-95 duration-150">
            <div className="w-12 h-12 mx-auto bg-red-100 rounded-full flex items-center justify-center text-red-600">
              <Trash2 className="w-6 h-6" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-black text-stone-900">Supprimer du dictionnaire ?</h3>
              <p className="text-xs text-stone-600 leading-relaxed">
                Voulez-vous vraiment supprimer définitivement le mot{' '}
                <strong className="text-stone-900 font-bold">« {wordToDelete.kab} »</strong> ({wordToDelete.fr}) ?
              </p>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2">
              <button
                type="button"
                disabled={isDeletingWord}
                onClick={() => {
                  playSound('click');
                  setWordToDelete(null);
                }}
                className="flex-1 py-2 bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold text-xs rounded-xl border border-stone-300 cursor-pointer transition-all"
              >
                Annuler
              </button>
              <button
                type="button"
                disabled={isDeletingWord}
                onClick={handleConfirmDeleteWord}
                className="flex-1 py-2 bg-red-600 hover:bg-red-700 text-white font-bold text-xs rounded-xl shadow-xs cursor-pointer transition-all flex items-center justify-center gap-1.5"
              >
                <Trash2 className="w-3.5 h-3.5" />
                <span>{isDeletingWord ? 'Suppression...' : 'Supprimer'}</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Training Shortcut/Tooltip */}
      {showFavoritesTooltip && (
        <div className="fixed bottom-12 left-1/2 -translate-x-1/2 z-50 animate-bounce">
          <button
            onClick={() => {
              playSound('click');
              setActiveDictionaryTab('VOCABULARY');
              setSelectedCategory('FAVORIS');
              setShowFavoritesTooltip(false);
              if (tooltipTimeoutRef.current) clearTimeout(tooltipTimeoutRef.current);
              tooltipTimeoutRef.current = null;
              
              // Scroll to top
              setTimeout(() => {
                const scrollContainer = document.getElementById('dictionary-main-scroll');
                if (scrollContainer) {
                  scrollContainer.scrollTo({ top: 0, behavior: 'smooth' });
                }
              }, 50);
            }}
            className="px-5 py-2.5 bg-gradient-to-r from-red-600 to-pink-600 hover:from-red-500 hover:to-pink-500 text-white font-black text-sm uppercase tracking-widest border-2 border-red-300 rounded-full shadow-[0_0_20px_rgba(239,68,68,0.8)] flex items-center gap-2 cursor-pointer transition-all active:scale-95"
          >
            <span>🎯</span>
            <span>S'entraîner</span>
          </button>
        </div>
      )}

      {/* Admin Add Public Word Modal */}
      {isAdmin && (
        <AddPublicWordModal
          isOpen={isAddWordModalOpen}
          onClose={() => setIsAddWordModalOpen(false)}
          onWordAdded={() => {
            setUpdateCounter((c) => c + 1);
          }}
          existingCategories={SORTED_CATEGORIES}
        />
      )}

      {/* Bottom Sticky Footer */}
      <footer className="py-1.5 px-3 sm:px-5 bg-[#ede4d4] border-t border-[#d6c7b0] flex items-center justify-center flex-shrink-0 z-30">
        <span className="text-[10px] text-stone-700 font-bold truncate text-center">
          {activeDictionaryTab === 'PRONUNCIATION'
            ? 'Lire en Kabyle (Aɣer) • Guide de Prononciation & Phonétique'
            : activeDictionaryTab === 'CUSTOM_VOCABULARY'
            ? 'Amawal-iw • Mon Vocabulaire Personnalisé & Quizz Sur-Mesure'
            : 'Amawal n Tmaziɣt • Dictionnaire & Vocabulaire'}
        </span>
      </footer>

      {/* Category Dropdown Drawer / Volet Déroulant Modal */}
      <AnimatePresence>
        {isCategoryDrawerOpen && (
          <div
            id="category-drawer-backdrop"
            className="fixed inset-0 z-[120] flex items-start sm:items-center justify-center p-3 sm:p-5 bg-black/65 backdrop-blur-xs overflow-y-auto select-none"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                playSound('click');
                setIsCategoryDrawerOpen(false);
              }
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: -20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.94, y: -20 }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              className="relative w-full max-w-2xl bg-[#fffdf9] border-2 border-amber-800/80 rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[88vh] my-auto"
            >
              {/* Drawer Header */}
              <div className="bg-gradient-to-r from-[#f5ecdd] to-[#ebdcc4] border-b border-[#dfd2be] px-4 py-3 flex items-center justify-between shrink-0">
                <div className="flex items-center gap-2.5">
                  <span className="text-2xl leading-none select-none">📂</span>
                  <div>
                    <h3 className="text-base font-black text-stone-900">
                      Volet des Catégories
                    </h3>
                    <p className="text-xs text-stone-600">
                      Parcourez et choisissez une thématique ({SORTED_CATEGORIES.length + 3} catégories au total)
                    </p>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    playSound('click');
                    setIsCategoryDrawerOpen(false);
                  }}
                  className="p-1.5 rounded-lg bg-white/80 hover:bg-white text-stone-700 hover:text-stone-950 border border-stone-300 shadow-2xs cursor-pointer transition-colors"
                  title="Fermer le volet"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>

              {/* Search Inside Volet */}
              <div className="p-3 bg-[#f8f2e6] border-b border-[#e6d8c3] shrink-0">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Filtrer une catégorie (ex: Animaux, Verbes, Maison...)"
                    value={categoryDrawerSearch}
                    onChange={(e) => setCategoryDrawerSearch(e.target.value)}
                    className="w-full bg-white border border-[#d6c7b2] rounded-xl px-3 py-2 pl-9 pr-8 text-base text-stone-900 focus:outline-none focus:border-amber-700 shadow-inner font-mono placeholder-stone-400"
                  />
                  <Search className="w-4 h-4 absolute left-3 top-2.5 text-stone-400 pointer-events-none" />
                  {categoryDrawerSearch && (
                    <button
                      type="button"
                      onClick={() => setCategoryDrawerSearch('')}
                      className="absolute right-2.5 top-2 text-stone-400 hover:text-stone-700 text-xs bg-stone-100 hover:bg-stone-200 px-1.5 py-0.5 rounded transition-colors cursor-pointer"
                    >
                      ✕
                    </button>
                  )}
                </div>
              </div>

              {/* Scrollable Categories List / Grid */}
              <div className="flex-1 overflow-y-auto p-3.5 sm:p-4 space-y-4">
                {/* Special Views Section */}
                {!categoryDrawerSearch && (
                  <div>
                    <h4 className="text-[11px] font-black uppercase tracking-wider text-amber-900 mb-2 px-1 flex items-center gap-1.5">
                      <span>✨</span>
                      <span>Vues Principales</span>
                    </h4>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      {/* TOUS */}
                      <button
                        type="button"
                        onClick={() => {
                          playSound('click');
                          setSelectedCategory('TOUS');
                          setIsCategoryDrawerOpen(false);
                          setCategoryDrawerSearch('');
                          document.getElementById('dictionary-main-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`p-3 min-h-[48px] rounded-xl border text-left flex items-center justify-between gap-2 transition-all cursor-pointer active:scale-98 ${
                          selectedCategory === 'TOUS'
                            ? 'bg-amber-800 text-white border-amber-950 shadow-md ring-2 ring-amber-400/60 font-black'
                            : 'bg-white hover:bg-amber-50 text-stone-800 border-[#dfd3c0] hover:border-amber-400 shadow-2xs font-bold'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span className="text-lg shrink-0">🌐</span>
                          <span className="text-xs sm:text-sm truncate">Tous les mots</span>
                        </div>
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                            selectedCategory === 'TOUS' ? 'bg-white/20 text-white' : 'bg-stone-100 text-stone-700 border border-stone-200'
                          }`}
                        >
                          {ALL_UNIQUE_WORDS.length}
                        </span>
                      </button>

                      {/* FAVORIS */}
                      <button
                        type="button"
                        onClick={() => {
                          playSound('click');
                          setSelectedCategory('FAVORIS');
                          setShowFavoritesTooltip(false);
                          setIsCategoryDrawerOpen(false);
                          setCategoryDrawerSearch('');
                          document.getElementById('dictionary-main-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`p-3 min-h-[48px] rounded-xl border text-left flex items-center justify-between gap-2 transition-all cursor-pointer active:scale-98 ${
                          selectedCategory === 'FAVORIS'
                            ? 'bg-rose-600 text-white border-rose-800 shadow-md ring-2 ring-rose-400/60 font-black'
                            : 'bg-rose-50/70 hover:bg-rose-100/80 text-rose-900 border-rose-200 hover:border-rose-400 shadow-2xs font-bold'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <Heart className="w-4 h-4 fill-current text-rose-500 shrink-0" />
                          <span className="text-xs sm:text-sm truncate">Favoris</span>
                        </div>
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                            selectedCategory === 'FAVORIS' ? 'bg-white/20 text-white' : 'bg-rose-200 text-rose-900'
                          }`}
                        >
                          {favoriteWordItems.length}
                        </span>
                      </button>

                      {/* SINGULIER_PLURIEL */}
                      <button
                        type="button"
                        onClick={() => {
                          playSound('click');
                          setSelectedCategory('SINGULIER_PLURIEL');
                          setIsCategoryDrawerOpen(false);
                          setCategoryDrawerSearch('');
                          document.getElementById('dictionary-main-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
                        }}
                        className={`p-3 min-h-[48px] rounded-xl border text-left flex items-center justify-between gap-2 transition-all cursor-pointer active:scale-98 ${
                          selectedCategory === 'SINGULIER_PLURIEL'
                            ? 'bg-amber-600 text-white border-amber-800 shadow-md ring-2 ring-amber-400/60 font-black'
                            : 'bg-amber-50/70 hover:bg-amber-100/80 text-amber-950 border-amber-200 hover:border-amber-400 shadow-2xs font-bold'
                        }`}
                      >
                        <div className="flex items-center gap-2 truncate">
                          <span className="text-lg shrink-0">📖</span>
                          <span className="text-xs sm:text-sm truncate">Singulier / Pluriel</span>
                        </div>
                        <span
                          className={`text-xs px-2.5 py-1 rounded-full font-bold ${
                            selectedCategory === 'SINGULIER_PLURIEL' ? 'bg-white/20 text-white' : 'bg-amber-200 text-amber-950'
                          }`}
                        >
                          {singularPluralCount}
                        </span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Thematic Categories Section */}
                <div>
                  <div className="flex items-center justify-between mb-2 px-1">
                    <h4 className="text-xs font-black uppercase tracking-wider text-amber-900 flex items-center gap-1.5">
                      <span>🏷️</span>
                      <span>Thématiques Kabyles ({filteredDrawerCategories.length})</span>
                    </h4>
                    {categoryDrawerSearch && (
                      <span className="text-xs text-stone-500 font-medium">
                        {filteredDrawerCategories.length} résultat{filteredDrawerCategories.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {filteredDrawerCategories.length === 0 ? (
                    <div className="text-center py-8 bg-stone-50 rounded-xl border border-dashed border-stone-300">
                      <p className="text-stone-500 text-sm">Aucune catégorie ne correspond à « {categoryDrawerSearch} »</p>
                      <button
                        type="button"
                        onClick={() => setCategoryDrawerSearch('')}
                        className="mt-2 text-xs text-amber-800 underline font-bold min-h-[36px]"
                      >
                        Effacer le filtre
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {filteredDrawerCategories.map((cat) => {
                        const isSelected = selectedCategory === cat;
                        const count = categoryCounts[cat] || 0;
                        const icon = CATEGORY_ICONS[cat] || '📁';
                        return (
                          <button
                            key={cat}
                            type="button"
                            onClick={() => {
                              playSound('click');
                              setSelectedCategory(cat);
                              setIsCategoryDrawerOpen(false);
                              setCategoryDrawerSearch('');
                              document.getElementById('dictionary-main-scroll')?.scrollTo({ top: 0, behavior: 'smooth' });
                            }}
                            className={`p-3 min-h-[48px] rounded-xl border text-left flex items-center justify-between gap-2 transition-all cursor-pointer active:scale-98 ${
                              isSelected
                                ? 'bg-amber-800 text-white border-amber-950 shadow-md ring-2 ring-amber-400/60 font-black'
                                : 'bg-white hover:bg-amber-50 text-stone-800 border-[#e3d7c5] hover:border-amber-400 shadow-2xs font-bold'
                            }`}
                          >
                            <div className="flex items-center gap-2 truncate">
                              <span className="text-lg shrink-0">{icon}</span>
                              <span className="text-xs sm:text-sm truncate">{cat}</span>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0">
                              <span
                                className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                                  isSelected ? 'bg-white/20 text-white' : 'bg-amber-100 text-amber-900'
                                }`}
                              >
                                {count}
                              </span>
                              {isSelected && <Check className="w-4 h-4 text-white shrink-0" />}
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              </div>

              {/* Drawer Footer */}
              <div className="bg-[#f5ecdd] border-t border-[#dfd2be] px-4 py-2.5 flex items-center justify-between text-xs text-stone-700 shrink-0">
                <span className="truncate mr-2">
                  Sélection : <strong className="text-amber-900">{selectedCategory === 'TOUS' ? 'Tous les mots' : selectedCategory === 'FAVORIS' ? 'Favoris' : selectedCategory === 'SINGULIER_PLURIEL' ? 'Singulier/Pluriel' : selectedCategory}</strong>
                </span>
                <button
                  type="button"
                  onClick={() => {
                    playSound('click');
                    setIsCategoryDrawerOpen(false);
                  }}
                  className="px-3.5 py-1 bg-amber-800 hover:bg-amber-900 text-white rounded-lg font-bold text-xs shadow-xs transition-colors cursor-pointer shrink-0"
                >
                  Fermer
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Schematic Gesture Coaching Overlay: Favorite Words Selection */}
      <AnimatePresence>
        {showHeartCoachingOverlay && (
          <motion.div
            key="dict-heart-coaching-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            onClick={dismissHeartOverlay}
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/65 backdrop-blur-xs cursor-pointer select-none"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.84, y: 12 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.88, y: -6 }}
              transition={{ type: 'spring', stiffness: 380, damping: 26 }}
              className="bg-stone-950/90 backdrop-blur-md text-white border-2 border-rose-500/40 shadow-2xl rounded-2xl p-5 sm:p-6 flex flex-col items-center justify-center gap-3 max-w-[280px] sm:max-w-[320px] text-center transition-transform active:scale-95"
            >
              {/* Animated Schematic Visual: Heart + Beating Glow + Touch Finger */}
              <div className="relative w-16 h-16 flex items-center justify-center select-none">
                {/* Gentle Pulsing Glow */}
                <motion.div
                  animate={{ scale: [1, 1.25, 1], opacity: [0.3, 0.7, 0.3] }}
                  transition={{ repeat: Infinity, duration: 1.6, ease: 'easeInOut' }}
                  className="absolute inset-0 bg-rose-500/30 rounded-full blur-md"
                />

                {/* Heart Icon Beating */}
                <motion.div
                  animate={{ scale: [1, 1.12, 1, 1.22, 1] }}
                  transition={{ repeat: Infinity, duration: 1.8, times: [0, 0.15, 0.3, 0.45, 1] }}
                  className="relative z-10 p-3 bg-gradient-to-br from-rose-500 to-red-600 rounded-2xl shadow-lg border border-rose-400/60 flex items-center justify-center text-white"
                >
                  <Heart className="w-8 h-8 fill-white text-white drop-shadow-[0_2px_4px_rgba(0,0,0,0.4)]" />
                </motion.div>

                {/* Touch Pointer Hand with pulse ripple */}
                <motion.div
                  animate={{ y: [6, -2, 6], x: [4, -1, 4] }}
                  transition={{ repeat: Infinity, duration: 1.5, ease: 'easeInOut' }}
                  className="absolute -bottom-1 -right-1 z-20 pointer-events-none"
                >
                  <span className="absolute -top-1 -left-1 w-4 h-4 bg-rose-400/50 rounded-full animate-ping" />
                  <span className="text-xl filter drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">👆</span>
                </motion.div>
              </div>

              {/* Text Instructions */}
              <div className="flex flex-col gap-1">
                <span className="text-sm sm:text-base font-black font-cinzel text-rose-300 tracking-wide">
                  Choisis tes mots favoris
                </span>
                <p className="text-xs text-stone-300 font-sans leading-relaxed">
                  Sélectionnez les mots sur lesquels vous souhaitez vous entraîner en cliquant sur l'icône <strong className="text-rose-400 font-bold">cœur ❤️</strong>.
                </p>
              </div>

              {/* Dismiss Pill */}
              <div className="mt-1 px-3 py-1 rounded-full bg-white/10 text-[10px] sm:text-xs text-stone-300 font-mono flex items-center gap-1.5 border border-white/15">
                <span>Touchez pour continuer</span>
                <span className="text-rose-400 font-bold">✓</span>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Thematic Challenge Quiz Modal (Lien direct vers les Défis Thématiques) */}
      {activeThematicChallenge && (
        <UnifiedQuizChallengeModal
          challengeId={activeThematicChallenge.id}
          title={`Défi : ${activeThematicChallenge.title}`}
          subtitle={`${activeThematicChallenge.npcName} • ${activeThematicChallenge.region}`}
          icon={activeThematicChallenge.icon || CATEGORY_ICONS[activeThematicChallenge.category] || '⚡'}
          words={getChallengeWords(activeThematicChallenge.category, playerId)}
          allWords={ALL_UNIQUE_WORDS}
          onClose={() => setActiveThematicChallenge(null)}
          onAddScore={onAddScore}
          initialHearts={5}
        />
      )}
    </div>
  );
});
